/* Create/edit modal for a RecurringInvoiceTemplate.
 *
 * Mounted by RecurringInvoices.js. Passing `template` puts the modal
 * in edit mode; passing null puts it in create mode.
 *
 * The form is one long scroll inside a centred card — three logical
 * sections (Customer, Items, Schedule) but no inner tabs. Tabs in a
 * modal this small are friction; a vertical scroll reads faster on
 * desktop AND on mobile bottom-sheet style.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import { useCurrency } from '../../contexts/CurrencyContext';
import './RecurringInvoiceModal.css';

const CADENCES = [
  { id: 'DAILY',     label: 'Daily'     },
  { id: 'WEEKLY',    label: 'Weekly'    },
  { id: 'BIWEEKLY',  label: 'Bi-weekly' },
  { id: 'MONTHLY',   label: 'Monthly'   },
  { id: 'QUARTERLY', label: 'Quarterly' },
  { id: 'YEARLY',    label: 'Yearly'    },
];

function toInputDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const tz = dt.getTimezoneOffset() * 60000;
  return new Date(dt.getTime() - tz).toISOString().slice(0, 10);
}

function blankLine() { return { description: '', qty: 1, unitPrice: 0 }; }

export default function RecurringInvoiceModal({ template, onClose, onSaved }) {
  const toast = useToast();
  const { formatInDisplayCurrency } = useCurrency();
  const isEdit = !!template;

  const [customerName,  setCustomerName]  = useState(template?.customerName  || '');
  const [customerEmail, setCustomerEmail] = useState(template?.customerEmail || '');
  const [customerPhone, setCustomerPhone] = useState(template?.customerPhone || '');
  const [notes,         setNotes]         = useState(template?.notes         || '');
  const [cadence,       setCadence]       = useState(template?.cadence       || 'MONTHLY');
  const [startsAt,      setStartsAt]      = useState(toInputDate(template?.startsAt) || toInputDate(new Date()));
  const [endsAt,        setEndsAt]        = useState(toInputDate(template?.endsAt));
  const [paymentTermsDays, setPaymentTermsDays] = useState(template?.paymentTermsDays ?? 7);
  const [lineItems,     setLineItems]     = useState(
    Array.isArray(template?.lineItems) && template.lineItems.length > 0
      ? template.lineItems.map((li) => ({
          description: li.description || '',
          qty: Number(li.qty) || 0,
          unitPrice: Number(li.unitPrice) || 0,
        }))
      : [blankLine()]
  );
  const [centralTax, setCentralTax] = useState({ enabled: false, rate: 0, name: 'VAT' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get('/merchant/payment-gateways/tax-settings')
      .then((res) => {
        if (alive && res.data?.status === 'success') {
          setCentralTax(res.data.data || { enabled: false, rate: 0, name: 'VAT' });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const li of lineItems) {
      const qty = Number(li.qty) || 0;
      const unit = Number(li.unitPrice) || 0;
      subtotal += qty * unit;
    }
    const rate = centralTax.enabled ? Number(centralTax.rate) || 0 : 0;
    const tax = Math.round((subtotal * rate) / 100 * 100) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [lineItems, centralTax]);

  const updateLine = useCallback((i, patch) => {
    setLineItems((prev) => prev.map((li, idx) => (idx === i ? { ...li, ...patch } : li)));
  }, []);
  const removeLine = useCallback((i) => {
    setLineItems((prev) => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));
  }, []);
  const addLine = useCallback(() => setLineItems((prev) => [...prev, blankLine()]), []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!customerName.trim() || !customerEmail.trim()) {
      toast.error('Customer name and email are required');
      return;
    }
    const cleanItems = lineItems
      .map((li) => ({
        description: String(li.description || '').trim(),
        qty: Number(li.qty) || 0,
        unitPrice: Number(li.unitPrice) || 0,
      }))
      .filter((li) => li.description && li.qty > 0);
    if (cleanItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    const payload = {
      customerName:     customerName.trim(),
      customerEmail:    customerEmail.trim(),
      customerPhone:    customerPhone.trim() || null,
      notes:            notes.trim() || null,
      cadence,
      startsAt:         startsAt ? new Date(startsAt).toISOString() : null,
      endsAt:           endsAt ? new Date(endsAt).toISOString() : null,
      paymentTermsDays: paymentTermsDays === '' ? null : Number(paymentTermsDays),
      lineItems:        cleanItems,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await axios.patch(`/merchant/recurring-invoices/${template.id}`, payload);
        toast.success('Template updated');
      } else {
        await axios.post('/merchant/recurring-invoices', payload);
        toast.success('Template created — first invoice will send on the start date');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [customerName, customerEmail, customerPhone, notes, cadence, startsAt, endsAt, paymentTermsDays, lineItems, isEdit, template, toast, onSaved]);

  return (
    <div className="rinvmod-overlay" onClick={onClose}>
      <div className="rinvmod-card" onClick={(e) => e.stopPropagation()}>
        <header className="rinvmod-head">
          <h2>{isEdit ? 'Edit recurring template' : 'New recurring template'}</h2>
          <button type="button" className="rinvmod-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <form className="rinvmod-form" onSubmit={handleSubmit}>
          <section>
            <div className="rinvmod-section-title">Customer</div>
            <div className="rinvmod-grid">
              <label>
                <span>Name *</span>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
              </label>
              <label>
                <span>Email *</span>
                <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required />
              </label>
              <label>
                <span>Phone</span>
                <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
              </label>
            </div>
          </section>

          <section>
            <div className="rinvmod-section-title">Line items</div>
            <div className="rinvmod-lines">
              <div className="rinvmod-line-head">
                <span>Description</span>
                <span className="rinvmod-num">Qty</span>
                <span className="rinvmod-num">Unit price</span>
                <span />
              </div>
              {lineItems.map((li, i) => (
                <div key={i} className="rinvmod-line">
                  <input
                    type="text"
                    placeholder="e.g., Monthly retainer"
                    value={li.description}
                    onChange={(e) => updateLine(i, { description: e.target.value })}
                  />
                  <input
                    type="number" min="0" step="1" className="rinvmod-num-input"
                    value={li.qty}
                    onChange={(e) => updateLine(i, { qty: e.target.value })}
                  />
                  <input
                    type="number" min="0" step="0.01" className="rinvmod-num-input"
                    value={li.unitPrice}
                    onChange={(e) => updateLine(i, { unitPrice: e.target.value })}
                  />
                  <button
                    type="button"
                    className="rinvmod-line-remove"
                    onClick={() => removeLine(i)}
                    disabled={lineItems.length === 1}
                    aria-label="Remove line"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button type="button" className="rinvmod-add-line" onClick={addLine}>
                <Plus size={14} /> Add line item
              </button>
            </div>

            <div className="rinvmod-totals">
              <div><span>Subtotal</span><span>{formatInDisplayCurrency(totals.subtotal)}</span></div>
              <div>
                <span>{centralTax.name || 'VAT'}{centralTax.enabled ? ` (${centralTax.rate || 0}%)` : ''}</span>
                <span>{formatInDisplayCurrency(totals.tax)}</span>
              </div>
              <small style={{ color: '#64748b' }}>
                VAT is centrally applied at invoice creation. Your business remains responsible for remittance.
              </small>
              <div className="rinvmod-totals-grand"><span>Total per cycle</span><span>{formatInDisplayCurrency(totals.total)}</span></div>
            </div>
          </section>

          <section>
            <div className="rinvmod-section-title">Schedule</div>
            <div className="rinvmod-grid">
              <label>
                <span>Cadence *</span>
                <select value={cadence} onChange={(e) => setCadence(e.target.value)}>
                  {CADENCES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <label>
                <span>Starts on *</span>
                <input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
              </label>
              <label>
                <span>Ends on (optional)</span>
                <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </label>
              <label>
                <span>Payment terms (days)</span>
                <input type="number" min="0" step="1" value={paymentTermsDays} onChange={(e) => setPaymentTermsDays(e.target.value)} />
              </label>
            </div>
          </section>

          <section>
            <div className="rinvmod-section-title">Notes (optional)</div>
            <textarea
              rows={3}
              placeholder="Visible to the customer on the invoice."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </section>

          <footer className="rinvmod-foot">
            <button type="button" className="rinvmod-btn rinvmod-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="rinvmod-btn rinvmod-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
